import React, { useEffect, useState } from "react";
import {
  Box,
  Collapse,
  Paper,
  Tabs,
  Tab,
  Stack,
  Typography,
  IconButton,
  Divider,
  Button,
  CircularProgress,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PhoneIcon from "@mui/icons-material/Phone";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import {
  CareGapStatusBadge,
  WorkflowStatusBadge,
  HistoricallyNonCompliantBadge,
  ContractualBadge,
} from "../shared/Badges";
import CareGapTimeline from "./CareGapTimeline";
import {
  getCareGapDetail,
  getCareGapHistory,
} from "../../../../utils/services/CareGapsV2/careGapsV2Service";
import { CARE_GAP_STATUS_COLORS } from "../../common/constants";

const fmtDate = (d) => {
  if (!d) return "—";
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return String(d);
  return x.toLocaleDateString();
};

const Field = ({ label, children }) => (
  <Box sx={{ minWidth: 140 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {children || "—"}
    </Typography>
  </Box>
);

export default function CareGapDetailsInline({
  open,
  careGap,
  onClose,
  onAction,
  refreshKey = 0,
}) {
  const [tab, setTab] = useState(0);
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (!open || !careGap?.careGapId) return;
    let cancelled = false;

    setTab(0);
    setLoadingDetail(true);
    setLoadingHistory(true);
    setDetail(null);
    setHistory([]);

    getCareGapDetail(careGap.careGapId)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e) => console.error("detail error", e))
      .finally(() => { if (!cancelled) setLoadingDetail(false); });

    getCareGapHistory(careGap.careGapId)
      .then((h) => { if (!cancelled) setHistory(Array.isArray(h) ? h : []); })
      .catch((e) => console.error("history error", e))
      .finally(() => { if (!cancelled) setLoadingHistory(false); });

    return () => { cancelled = true; };
  }, [open, careGap?.careGapId, refreshKey]);

  const data = detail || careGap || {};
  const statusKey = data.careGapStatusKey || "open";
  const statusColors = CARE_GAP_STATUS_COLORS[statusKey] || CARE_GAP_STATUS_COLORS.open;

  return (
    <Collapse in={open} timeout={350} unmountOnExit>
      <Fade in={open} timeout={500}>
        <Paper
          elevation={6}
          sx={{
            mt: -0.5,
            ml: { xs: 0, sm: 3 },
            mr: { xs: 0, sm: 1 },
            mb: 1,
            borderRadius: "0 0 12px 12px",
            borderLeft: `5px solid ${statusColors.border}`,
            borderTop: `1px dashed ${statusColors.border}`,
            overflow: "hidden",
            bgcolor: "background.paper",
          }}
        >
          <Box
            sx={{
              px: 2,
              pt: 1.5,
              pb: 1,
              bgcolor: statusColors.bg,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
                  Care gap detail
                </Typography>
                <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                  {data.hedisDisplayName || data.careGapName || "—"}
                </Typography>
              </Box>
              <IconButton size="small" onClick={onClose} aria-label="Collapse">
                <KeyboardArrowUpIcon />
              </IconButton>
              <IconButton size="small" onClick={onClose} aria-label="Close">
                <CloseIcon />
              </IconButton>
            </Stack>

            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
              <CareGapStatusBadge
                statusKey={data.careGapStatusKey}
                statusName={data.careGapStatusName}
              />
              <WorkflowStatusBadge
                workflowKey={data.workflowStatusKey}
                workflowName={data.workflowStatusName}
              />
              {data.contractual ? <ContractualBadge qmCode={data.qmCode} /> : null}
              {data.historicallyNonCompliant ? <HistoricallyNonCompliantBadge /> : null}
            </Stack>
          </Box>

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 40,
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 40, py: 0.5 },
            }}
          >
            <Tab label="Overview" />
            <Tab label="Patient" />
            <Tab label="Timeline" />
          </Tabs>

          <Box sx={{ p: 2, minHeight: 160 }}>
            {loadingDetail && tab !== 2 ? (
              <Stack alignItems="center" sx={{ py: 3 }}>
                <CircularProgress size={24} />
              </Stack>
            ) : (
              <>
                {tab === 0 && <OverviewTab data={data} />}
                {tab === 1 && <PatientTab data={data} />}
                {tab === 2 && <CareGapTimeline history={history} loading={loadingHistory} />}
              </>
            )}
          </Box>

          <Divider />

          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ px: 2, py: 1.5 }}>
            <Button
              size="small"
              startIcon={<CalendarMonthIcon />}
              variant="contained"
              onClick={() => onAction?.("schedule", data)}
            >
              Schedule
            </Button>
            <Button
              size="small"
              startIcon={<PhoneIcon />}
              variant="outlined"
              onClick={() => onAction?.("outreach", data)}
            >
              Outreach
            </Button>
            <Button
              size="small"
              startIcon={<UploadFileIcon />}
              variant="outlined"
              color="success"
              onClick={() => onAction?.("submit", data)}
            >
              Submit
            </Button>
            <Button
              size="small"
              startIcon={<MoreVertIcon />}
              variant="outlined"
              color="inherit"
              onClick={() => onAction?.("workflow", data)}
            >
              Workflow
            </Button>
          </Stack>
        </Paper>
      </Fade>
    </Collapse>
  );
}

function OverviewTab({ data }) {
  return (
    <Stack spacing={2}>
      <Stack direction="row" flexWrap="wrap" gap={3}>
        <Field label="Measurement year">{data.measurementYear}</Field>
        <Field label="Measure code">{data.measureCode}</Field>
        <Field label="QM">{data.qmCode}</Field>
        <Field label="Category">{data.hedisCategory}</Field>
        {data.peerRate != null && (
          <Field label="BCBS peer rate">{(Number(data.peerRate) * 100).toFixed(2)}%</Field>
        )}
      </Stack>

      <Divider />

      <Typography variant="subtitle2" fontWeight={700}>
        Assignment
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={3}>
        <Field label="PCP">{data.pcpFullName}</Field>
        <Field label="Location">{data.locationName}</Field>
        <Field label="Office">{data.officeLocation}</Field>
      </Stack>

      <Divider />

      <Typography variant="subtitle2" fontWeight={700}>
        Key dates
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={3}>
        <Field label="Workflow updated">{fmtDate(data.workflowUpdated)}</Field>
        <Field label="Submitted date">{fmtDate(data.submittedDate)}</Field>
        <Field label="Closure date">{fmtDate(data.serviceDateOfClosure)}</Field>
        <Field label="Last seen (sync)">{fmtDate(data.lastSeen)}</Field>
      </Stack>
    </Stack>
  );
}

function PatientTab({ data }) {
  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle1" fontWeight={800}>
          {data.patientFullName || "—"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          CIP {data.cipId} {data.memberId ? `· Member ${data.memberId}` : ""}
        </Typography>
      </Box>

      <Divider />

      <Stack direction="row" flexWrap="wrap" gap={3}>
        <Field label="DOB">{fmtDate(data.patientDob)}</Field>
        <Field label="Age">{data.patientAge}</Field>
        <Field label="Gender">{data.patientGender}</Field>
      </Stack>

      <Divider />

      <Typography variant="subtitle2" fontWeight={700}>
        Contact
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={3}>
        <Field label="Phone">{data.patientPhone || "—"}</Field>
        <Field label="Mobile">{data.patientMobile || "—"}</Field>
        <Field label="Email">{data.patientEmail || "—"}</Field>
      </Stack>
    </Stack>
  );
}
